import React, { useState, useCallback, useEffect, useRef } from 'react';
import UpdateFunding from './UpdateFunding';
import UpdateGoals from './UpdateGoals';
import {
    DEFAULT_GOAL_CATEGORY,
    FEATURE_NAMES,
    TermTabType
} from '../../common/constants';
import { useAppSelector, useAppDispatch, useFundingTermScope } from '../../common/hooks';
import { updateClientInvestmentSources } from '../../features/client';
import { syncAdviceGoals } from '../../features/client-goals';
import { updateGoalContinuity } from '../../features/ews';
import { recordUnsavedTermChanges } from '../../features/unsaved-plan-changes';
import { checkFFEnabled, retrySingleEWS } from '../../utils';
import { goalCategoryPortfolioMappingsType } from 'common/types';

interface UpdateFundingGoalsProps {
    displayedFormFields: any;
    selectedTermIndex: TermTabType;
    isFundingGoalsModalOpen: boolean;
    setIsFundingGoalsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    currentRunpipeData: any;
}

const hasGoalAmountChanged = (prior: any, updated: any): boolean =>
    prior.goalAmount !== updated.goalAmount || prior.targetedRetirementIncome !== updated.targetedRetirementIncome;

const UpdateFundingGoals: React.FC<UpdateFundingGoalsProps> = ({
    displayedFormFields,
    selectedTermIndex,
    isFundingGoalsModalOpen,
    setIsFundingGoalsModalOpen,
    currentRunpipeData
}) => {
    const [isGoalsModalOpen, setIsGoalsModalOpen] = useState(false);
    const [isFundingModalOpen, setIsFundingModalOpen] = useState(false);
    // AEX-3724: like the console's Review Goals -> Reallocate flow, step 1 <-> step 2 swap their dialogs
    // instantly (no fade out/in) so it reads as one modal; opening/closing the flow keeps the default fade.
    const [isSwitchingStep, setIsSwitchingStep] = useState(false);
    const stepTransitionDuration = isSwitchingStep ? 0 : undefined;
    const adviceClient = useAppSelector((state) => state.adviceClient);
    const { incomeSources, investmentsSources } = adviceClient?.accounts || {};
    const adviceGoals = useAppSelector((state) => state.adviceGoals || []);
    const incomeSourceType = useAppSelector((state) => state.global?.orgInfo?.incomeSourceType?.en);
    const investmentAccountType = useAppSelector((state) => state.global?.orgInfo?.investmentAccountType?.en);
    const orgEmail = useAppSelector((state) => state.global?.orgInfo?.orgEmail || '');
    const goalCategoryPortfolioMappings: goalCategoryPortfolioMappingsType | undefined = useAppSelector(
        (state) => state?.global?.globalConfig?.goalCategoryPortfolioMappings
    );
    const reallocSchedules = useAppSelector((state) => state.global?.goeConfig?.reallocSchedules);
    const [filteredInvestmentAccounts, setFilteredInvestmentAccounts] = useState<any[]>([]);
    const isFundingEditedRef = useRef(false);
    const isGoalsEditedRef = useRef(false);
    const dispatch = useAppDispatch();
    const { durationKey: currentDurationKey, duration: currentDuration } = useFundingTermScope(selectedTermIndex);

    useEffect(() => {
        setIsFundingModalOpen(isFundingGoalsModalOpen);
    }, [isFundingGoalsModalOpen]);

    useEffect(() => {
        setFilteredInvestmentAccounts(
            (investmentsSources ?? [])
                .map((src) => ({ ...src }))
                .filter((src: any) =>
                    investmentAccountType?.some(
                        (t: any) =>
                            t?.value === src?.accountType &&
                            (currentDuration === DEFAULT_GOAL_CATEGORY || t?.duration === currentDuration)
                    )
                )
        );
    }, [investmentsSources, investmentAccountType, currentDuration, isFundingGoalsModalOpen]);

    const handleCloseFundingModal = (): void => {
        if (setIsFundingGoalsModalOpen) {
            setIsFundingGoalsModalOpen(false);
        }
        setIsSwitchingStep(false);
        setIsFundingModalOpen(false);
        setFilteredInvestmentAccounts([]);
    };

    const handleCloseGoalsModal = (): void => {
        if (setIsFundingGoalsModalOpen) {
            setIsFundingGoalsModalOpen(false);
        }
        setIsSwitchingStep(false);
        setIsGoalsModalOpen(false);
        setFilteredInvestmentAccounts([]);
    };
    const handleOpenGoalsModal = useCallback((): void => {
        setIsSwitchingStep(true);
        setIsFundingModalOpen(false);
        setIsGoalsModalOpen(true);
    }, []);

    const handleBackToFunding = useCallback((): void => {
        setIsSwitchingStep(true);
        setIsGoalsModalOpen(false);
        setIsFundingModalOpen(true);
    }, []);

    const handleSaveUpdate = useCallback(
        async (updatedFormFields: any[]): Promise<void> => {
            const isFundingEdited = isFundingEditedRef.current;
            const isGoalsEdited = isGoalsEditedRef.current;
            if (isFundingEdited || isGoalsEdited) {
                // update redux accounts state
                const updatedInvestmentSources = investmentsSources.map((src) => {
                    const editedSource = filteredInvestmentAccounts.find((acc: any) => acc.accountId === src.accountId);
                    return editedSource ? { ...src, ...editedSource } : src;
                });

                await dispatch(updateClientInvestmentSources(updatedInvestmentSources));

                // update redux goal state
                const updatedGoalsData = Object.assign({}, adviceGoals);
                let updatedGoals = updatedGoalsData?.formFields.map((field: any) => {
                    const existingGoal = updatedFormFields.find((goal: any) => goal.goalId === field.goalId);
                    return existingGoal ? { data: { ...existingGoal, ...field }, goalId: field.goalId } : field;
                });
                updatedGoalsData.formFields = updatedGoals;
                await dispatch(syncAdviceGoals(updatedGoalsData));

                const findPriorGoalData = (goalId: string) =>
                    adviceGoals?.formFields?.find((field: any) => field.goalId === goalId)?.data;
                const accountsChanged = updatedInvestmentSources.some(
                    (source, index) => Number(source.amount) !== Number(investmentsSources[index]?.amount)
                );
                const goalsChanged = updatedFormFields.some((updatedGoal: any) => {
                    const priorData = findPriorGoalData(updatedGoal.goalId);
                    return Boolean(priorData) && hasGoalAmountChanged(priorData, updatedGoal);
                });
                dispatch(
                    recordUnsavedTermChanges({
                        accounts: accountsChanged ? [currentDurationKey] : [],
                        goals: goalsChanged ? [currentDurationKey] : []
                    })
                );

                if (isGoalsEdited && checkFFEnabled(FEATURE_NAMES['CONTINUITY_OF_ADVICE_FE_AEX-3603'])) {
                    updatedFormFields.forEach((updatedGoal: any) => {
                        const priorData = findPriorGoalData(updatedGoal.goalId);
                        if (!priorData) return;
                        if (hasGoalAmountChanged(priorData, updatedGoal)) {
                            dispatch(
                                updateGoalContinuity({ goalId: updatedGoal.goalId, changes: { isNewGoal: true } })
                            );
                        }
                    });
                }

                setIsGoalsModalOpen(false);
                setIsFundingModalOpen(false);
                if (setIsFundingGoalsModalOpen) {
                    setIsFundingGoalsModalOpen(false);
                }

                const updatedAdviceClient = {
                    ...adviceClient,
                    accounts: {
                        ...adviceClient.accounts,
                        incomeSources: incomeSources,
                        investmentsSources: updatedInvestmentSources
                    }
                };
                await retrySingleEWS(
                    dispatch,
                    incomeSourceType,
                    investmentAccountType,
                    updatedAdviceClient,
                    orgEmail,
                    currentDurationKey,
                    goalCategoryPortfolioMappings,
                    reallocSchedules
                );
            } else {
                setIsGoalsModalOpen(false);
                setIsFundingModalOpen(false);
                if (setIsFundingGoalsModalOpen) {
                    setIsFundingGoalsModalOpen(false);
                }
            }
        },
        [
            dispatch,
            incomeSources,
            investmentsSources,
            filteredInvestmentAccounts,
            adviceGoals,
            adviceClient,
            incomeSourceType,
            investmentAccountType,
            orgEmail,
            currentDurationKey
        ]
    );

    return (
        <>
            <UpdateFunding
                investmentAccounts={filteredInvestmentAccounts}
                isOpen={isFundingModalOpen || false}
                onClose={() => {
                    handleCloseFundingModal();
                    isFundingEditedRef.current = false;
                }}
                onSubmit={(isEdited, values) => {
                    console.log('Updated Funding & Goals:', { isEdited, values });
                    isFundingEditedRef.current = isEdited;
                    setFilteredInvestmentAccounts(values.accounts);
                    handleOpenGoalsModal();
                }}
                selectedTermIndex={selectedTermIndex}
                transitionDuration={stepTransitionDuration}
            />
            <UpdateGoals
                formFieldDataSource={displayedFormFields}
                isOpen={isGoalsModalOpen}
                onClose={() => {
                    handleCloseGoalsModal();
                    isGoalsEditedRef.current = false;
                }}
                onSubmit={(isEdited, values) => {
                    console.log('Updated Goals:', { isEdited, values });
                    isGoalsEditedRef.current = isEdited;
                    handleSaveUpdate(values);
                }}
                handleBackToFunding={handleBackToFunding}
                currentRunpipeData={currentRunpipeData}
                selectedTermIndex={selectedTermIndex}
                transitionDuration={stepTransitionDuration}
            />
        </>
    );
};

export default UpdateFundingGoals;
